import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Wallet, Search } from "lucide-react";
import { listMyFinance } from "@/lib/api/parent-portal.functions";
import { paymentMethodLabel } from "@/lib/payment-methods";
import { LoadErrorState } from "@/components/parent/LoadErrorState";
import { describeLoadError } from "@/lib/browser-storage";

export const Route = createFileRoute("/_authenticated/responsavel/financeiro")({
  head: () => ({
    meta: [
      { title: "Ficha Financeira | FutBux" },
      { name: "description", content: "Cobranças, pagamentos e pedidos dos seus alunos." },
      { property: "og:title", content: "Ficha Financeira | FutBux" },
      { property: "og:description", content: "Cobranças, pagamentos e pedidos dos seus alunos." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const fmtBRL = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const paidLike = (s?: string | null) => s === "pago" || s === "conferido" || s === "confirmado";

function StatusBadge({ status }: { status: string }) {
  const paid = paidLike(status);
  const label = paid ? "Pago" : status === "rejeitado" ? "Rejeitado" : "Pendente";
  return <Badge variant={paid ? "default" : status === "rejeitado" ? "destructive" : "secondary"}>{label}</Badge>;
}

function ymOf(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function Page() {
  const fn = useServerFn(listMyFinance);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fn());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const rows: any[] = [];
    (data.proofs ?? []).forEach((p: any) => rows.push({
      key: `proof:${p.id}`, kind: p.source ?? "cobranca",
      title: p.note ?? "Cobrança",
      date: p.created_at, amount: Number(p.verified_amount ?? p.amount ?? 0), status: p.status, method: null,
    }));
    (data.productOrders ?? []).forEach((o: any) => rows.push({
      key: `prod:${o.id}`, kind: "produto",
      title: `${o.products?.name ?? "Produto"} ×${o.quantity ?? 1}`,
      date: o.created_at, amount: Number(o.amount ?? 0), status: o.status, method: o.payment_method,
    }));
    (data.uniformOrders ?? []).forEach((o: any) => rows.push({
      key: `uni:${o.id}`, kind: "uniforme",
      title: `${o.uniforms?.name ?? "Uniforme"} (${o.size ?? "-"})`,
      date: o.created_at, amount: Number(o.amount ?? 0), status: o.status, method: o.payment_method,
    }));
    return rows.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  }, [data]);

  const months = useMemo(() => {
    const set = new Set(items.map((i) => ymOf(i.date)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [items]);

  const filtered = useMemo(() => items.filter((i) => {
    if (kindFilter !== "all" && i.kind !== kindFilter) return false;
    if (statusFilter === "pago" && !paidLike(i.status)) return false;
    if (statusFilter === "pendente" && (paidLike(i.status) || i.status === "rejeitado")) return false;
    if (statusFilter === "rejeitado" && i.status !== "rejeitado") return false;
    if (monthFilter !== "all" && ymOf(i.date) !== monthFilter) return false;
    if (q && !`${i.title}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, kindFilter, statusFilter, monthFilter, q]);

  const totalPending = filtered.filter((i) => !paidLike(i.status) && i.status !== "rejeitado").reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = filtered.filter((i) => paidLike(i.status)).reduce((s, i) => s + (i.amount || 0), 0);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;

  if (error) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <LoadErrorState
          rawError={error}
          message={describeLoadError(error, "Erro ao carregar a ficha financeira.")}
          onRetry={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Wallet className="h-6 w-6" /> Ficha Financeira</h1>
        <p className="text-muted-foreground text-sm">Cobranças, pagamentos e pedidos.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card><CardHeader className="pb-1"><CardTitle className="text-sm text-muted-foreground">Pendente</CardTitle></CardHeader>
          <CardContent className="text-xl font-bold text-amber-600">{fmtBRL(totalPending)}</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardTitle className="text-sm text-muted-foreground">Pago</CardTitle></CardHeader>
          <CardContent className="text-xl font-bold text-emerald-600">{fmtBRL(totalPaid)}</CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="border rounded-md h-9 px-2 text-sm bg-background" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
          <option value="all">Todos tipos</option>
          <option value="cobranca">Cobrança</option>
          <option value="produto">Produto</option>
          <option value="uniforme">Uniforme</option>
          <option value="rental">Reserva</option>
        </select>
        <select className="border rounded-md h-9 px-2 text-sm bg-background" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Todos status</option>
          <option value="pendente">Pendente</option>
          <option value="pago">Pago</option>
          <option value="rejeitado">Rejeitado</option>
        </select>
        <select className="border rounded-md h-9 px-2 text-sm bg-background" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
          <option value="all">Todos meses</option>
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-9" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="p-6 text-sm text-muted-foreground">Sem lançamentos.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((i) => (
            <Card key={i.key}>
              <CardContent className="flex items-center justify-between gap-3 p-3 text-sm">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="uppercase text-[10px]">{i.kind}</Badge>
                    <b className="truncate">{i.title}</b>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {i.date ? new Date(i.date).toLocaleDateString("pt-BR") : "-"}
                    {i.method ? ` • ${paymentMethodLabel(i.method)}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{fmtBRL(i.amount)}</div>
                  <StatusBadge status={i.status} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
